import type { SharedUrl, ViewFieldsFragment } from '@salescore/client-api'
import {
  isSharedLinkPresetName,
  makeSharedLinkPresetName,
  viewConfigFieldSchema,
  viewConfigFilterNodeSchema,
  viewConfigMetaSchema,
  type ViewConfigSheet,
  type ViewConfigSheetPreset,
  viewConfigSorterSchema,
} from '@salescore/core'
import { isNotNullish } from 'effect/Predicate'
import { z } from 'zod'

/** 発行ボタン経由の共有 parameter。発行時の絞り込み・並び替え・カラム構成・表示設定を全量持つ (未設定の項目は含まれない)。 */
const sheetSharedUrlParameterSchema = z.object({
  filterTree: viewConfigFilterNodeSchema.optional(),
  sorters: viewConfigSorterSchema.array().optional(),
  fields: viewConfigFieldSchema.array().optional(),
  meta: viewConfigMetaSchema.optional(),
  sheetPresetName: z.string().optional(),
})

export type SheetSharedUrlParameter = z.infer<typeof sheetSharedUrlParameterSchema>

/** コメント経由の共有 parameter。レコードの絞り込み (filterTree) のみを持ち、並び替え等は閲覧側の既存設定を維持する。 */
const sheetThreadCommentParameterSchema = z.object({
  filterTree: viewConfigFilterNodeSchema,
  sheetPresetName: z.string().optional(),
})

/** シートに適用する共有リンクの種類。コメント経由か、発行ボタン経由の単独リンクか。 */
type SheetSharedUrlKind = 'sheetThreadComment' | 'sheetSharedUrl'

/** ダッシュボード (kpiPivot) に適用する共有リンクの種類。発行ボタン経由の単独リンクか、KPI ドリルダウン先シートのコメント経由か。 */
export type KpiPivotSharedUrlKind = 'kpiPivotSharedUrl' | 'sheetThreadCommentInKpiPivotDrillDown'

export type SharedUrlKind = SheetSharedUrlKind | KpiPivotSharedUrlKind

export interface AppliedSharedUrlView {
  view: ViewFieldsFragment
  kind?: SharedUrlKind
}

export const applySharedUrlToSheetView = (view: ViewFieldsFragment, sharedUrl: SharedUrl): AppliedSharedUrlView => {
  if (view.config.type !== 'sheet') {
    return { view, kind: undefined }
  }
  // fetchSharedUrl はコメント無しのとき sheetThreadComment を null で返すため、null / undefined を「コメント無し」として扱う
  return isNotNullish(sharedUrl.sheetThreadComment)
    ? applySheetThreadCommentSharedUrl(view, view.config, sharedUrl)
    : applySheetPublishedSharedUrl(view, view.config, sharedUrl)
}

/**
 * コメント経由: レコードの絞り込みのみ適用し、並び替え・カラム構成・表示設定は既存を維持する。
 * KPI ドリルダウン先シートのコメントは sharedUrl.viewId が kpiPivot 側を指すため、単独リンクと違い viewId の一致は見ない。
 */
const applySheetThreadCommentSharedUrl = (
  view: ViewFieldsFragment,
  config: ViewConfigSheet,
  sharedUrl: SharedUrl,
): AppliedSharedUrlView => {
  // filterTree はコメント投稿時に必ず保存されるため、
  // 持たない parameter は絞り込み無しの [共有リンク] プリセットを誤って注入しないよう弾く
  const parameter = sheetThreadCommentParameterSchema.safeParse(sharedUrl.parameter)
  if (!parameter.success) {
    return { view, kind: undefined }
  }
  const { filterTree, sheetPresetName } = parameter.data
  const sharedLinkPreset: ViewConfigSheetPreset = {
    name: makeSharedLinkPresetName(sheetPresetName),
    tree: config.tree,
    filterTree,
  }
  const presets = buildPresetsWithSharedLink(config.presets, sharedLinkPreset)
  return {
    kind: 'sheetThreadComment',
    view: { ...view, config: { ...config, filterTree, presets } },
  }
}

/** 単独リンク: 発行時の状態を忠実に反映する (発行時に未設定だった項目は未設定として反映)。 */
const applySheetPublishedSharedUrl = (
  view: ViewFieldsFragment,
  config: ViewConfigSheet,
  sharedUrl: SharedUrl,
): AppliedSharedUrlView => {
  // ドリルダウンでは同一タブ内で shareId (redux) が保持され、別 view の sharedUrl がドリルダウン先の sheet にも渡るため、viewId 不一致は弾く
  if (view.id !== sharedUrl.viewId) {
    return { view, kind: undefined }
  }
  const parameter = sheetSharedUrlParameterSchema.safeParse(sharedUrl.parameter)
  if (!parameter.success) {
    return { view, kind: undefined }
  }
  const { fields, filterTree, meta, sheetPresetName, sorters } = parameter.data
  const sharedLinkPreset: ViewConfigSheetPreset = {
    name: makeSharedLinkPresetName(sheetPresetName),
    tree: config.tree,
    filterTree,
    sorters,
    fields,
    meta,
  }
  const presets = buildPresetsWithSharedLink(config.presets, sharedLinkPreset)
  return {
    kind: 'sheetSharedUrl',
    view: { ...view, config: { ...config, presets, filterTree, sorters, fields, meta } },
  }
}

/** 再共有で [共有リンク] プリセットが蓄積しないよう既存の共有リンクプリセットを除き、先頭に注入する。 */
const buildPresetsWithSharedLink = (
  presets: ViewConfigSheetPreset[] | undefined,
  sharedLinkPreset: ViewConfigSheetPreset,
): ViewConfigSheetPreset[] => {
  const presetsWithoutSharedLink = presets?.filter((preset) => !isSharedLinkPresetName(preset.name)) ?? []
  return [sharedLinkPreset, ...presetsWithoutSharedLink]
}
